import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@d12labs/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import {
	CATEGORY_COLORS,
	CATEGORY_KEYS,
	CATEGORY_LABELS,
	type CategoryKey,
	type TrendPoint,
} from "../types";

export type TrendSeriesKey = "overall" | CategoryKey;

/** All selectable series, in display order — overall first, then the 6 KPIs. */
export const TREND_SERIES: { key: TrendSeriesKey; label: string; color: string }[] = [
	{ key: "overall", label: "Overall", color: "var(--color-foreground)" },
	...CATEGORY_KEYS.map((k) => ({ key: k, label: CATEGORY_LABELS[k], color: CATEGORY_COLORS[k] })),
];

const config: ChartConfig = Object.fromEntries(
	TREND_SERIES.map((s) => [s.key, { label: s.label, color: s.color }]),
);

/**
 * Score progression over time (one point per analysis, oldest-first). Renders a
 * line per `active` series. Category scores are 0–10; they're normalized ×10 so
 * they share the overall score's 0–100 axis.
 */
export function TrendChart({ series, active }: { series: TrendPoint[]; active: TrendSeriesKey[] }) {
	const data = series.map((p) => {
		const row: Record<string, string | number> = {
			label: new Date(p.meetingDate).toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
			}),
			overall: p.overallScore,
		};
		for (const key of CATEGORY_KEYS) row[key] = (p.categories[key] ?? 0) * 10;
		return row;
	});

	return (
		<ChartContainer config={config} className="aspect-[3/1] w-full">
			<LineChart data={data} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
				<CartesianGrid vertical={false} />
				<XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
				<YAxis
					domain={[0, 100]}
					tickLine={false}
					axisLine={false}
					width={28}
					tick={{ fontSize: 11 }}
				/>
				<ChartTooltip content={<ChartTooltipContent />} />
				{TREND_SERIES.filter((s) => active.includes(s.key)).map((s) => (
					<Line
						key={s.key}
						dataKey={s.key}
						type="monotone"
						stroke={`var(--color-${s.key})`}
						strokeWidth={s.key === "overall" ? 2.5 : 1.5}
						dot={{ r: s.key === "overall" ? 3 : 2 }}
						isAnimationActive={false}
					/>
				))}
			</LineChart>
		</ChartContainer>
	);
}
